import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import {
  isRefinance,
  mapInput,
  loan_computation,
  rental_loan_computation,
  isLongTermRental,
  isShortTermFixAndFlip,
  normalizeQuotes,
} from '../../../../../utils/functions';
import { trackEvent, InputUpdated } from '../../../../../utils/mixpanel';
import { isBelowMinLoanAmount, getMinLoanAmount } from '../utils';

interface UseLoanComputationProps {
  values: any;
  setValues: (values: any) => void;
  selectedProgram: any;
  PROGRAMS: any[];
}

export const useLoanComputation = ({ values, setValues, selectedProgram, PROGRAMS }: UseLoanComputationProps) => {
  const [loanOptions, setLoanOptions] = useState<any>(undefined)
  const [multipleLoanOptions, setMultipleLoanOptions] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)

  /* Loan balance variables */
  const maxLoanBalance = useRef<number | undefined>(undefined)
  const [loanBalance, setLoanBalance] = useState<number>(0)
  
  const minLTV = useRef<number | undefined>(undefined)
  const min_ltv = minLTV.current
  const maxLTV = useRef<number | undefined>(undefined)
  const max_ltv = maxLTV.current
  const [ltv, setLtv] = useState<number>(0)

  /* We keep state of the old values */
  const lastValues = useRef<any>(undefined)
  const lastLoanBalance = useRef<number | undefined>(undefined)
  const lastLtv = useRef<number | undefined>(undefined)
  const lastSelectedProgram = useRef<any>(undefined)
  const currentValuesRef = useRef(values)

  const {loanBalance: _, ...coreValues} = values
  const {loanBalance: __, ...lastCoreValues} = lastValues.current || {}

  /* Some changes were made in the options, we need to clear */
  const valuesHaveChanged = !isEqual(values, lastValues.current)
  const coreValuesHaveChanged = !isEqual(coreValues, lastCoreValues)

  /* When values change we trigger the call to the API */
  useEffect(() => {
    if (!lastValues.current) {
      lastValues.current = values
      return
    }
    
    if (coreValuesHaveChanged) {
      delete values["loanBalance"]
    }

    if (valuesHaveChanged) {
      const changedKey = Object.keys(values).find((k) => values[k] !== lastValues.current?.[k])
      if (changedKey) {
        trackEvent(InputUpdated(changedKey, values[changedKey]))
      }
    }

    const lastSelectedProgramHasChanged = !isEqual(selectedProgram, lastSelectedProgram.current)

    /* And we need to keep track of record */
    lastValues.current = values
    lastSelectedProgram.current = selectedProgram

    if (rental_loan_computation && loan_computation && (valuesHaveChanged || lastSelectedProgramHasChanged)) {
      if (!isRefinance(values)) {
        delete values["currentLoanBalance"]
      }

      setIsLoading(true)
      setLoanOptions(undefined)
      
      // Check if this is a rental loan - use direct check for reliability
      const condition = normalizeQuotes(values["What condition is the property in?"])?.trim()
      const length = normalizeQuotes(values["What is your ideal loan length?"])?.trim()
      const isRentalLoan = (condition === "It's stabilized and/or ready to rent" && 
                            length === "Long-term (30 years)") || 
                           isLongTermRental(values)
      
      if (isRentalLoan) {
        rental_loan_computation(
          /* If there is loan balance there is not an optimization */
          { ...mapInput({ ...values }), ...(selectedProgram.key === PROGRAMS[0].key ? {} : { program: selectedProgram.key }) },
          (results) => {
            const firstResult = results[0]
            const list = Array.isArray(firstResult) ? firstResult : []
            setMultipleLoanOptions(list)
            if (firstResult) {
              const result = Array.isArray(firstResult) ? firstResult[0] : firstResult
              /* We store and update the data */
              setLoanOptions(result)
              setLtv(result.ltv)
              setLoanBalance(result.loan_amount)
              /* We keep track of the maximum on the call for those specific values */
              /* We can do as many calls as we want after this, when loan amount changes */
              /* And loan amount changes when LTV is updated, or when it's directly updated */
              if (!maxLoanBalance.current || coreValuesHaveChanged) {
                maxLoanBalance.current = (result.loan_amount)
              }
              if (!maxLTV.current || coreValuesHaveChanged) {
                maxLTV.current = (result.ltv)
              }
              if (!minLTV.current || coreValuesHaveChanged) {
                minLTV.current = result.min_ltv ?? result.ltv
              }
              /* We also update this so it does not trigger a rerender */
              lastLoanBalance.current = result.loan_amount
              lastLtv.current = result.ltv
            }
            setIsLoading(false)
          }
        )
      } else {
        loan_computation(
          {...mapInput({ ...values })},
          (results) => {
            const firstResult = results[0]
            // some backends return nested arrays; ensure we store an array of options
            const list = Array.isArray(firstResult) ? firstResult : results
            setMultipleLoanOptions(list || [])
            if (firstResult) {
              const result = Array.isArray(firstResult) ? firstResult[0] : firstResult
              const newLoanBalance = result.total_loan_amount || result.advance_amount_max
              /* We store and update the data */
              setLoanOptions(result)
              setLtv(result.ltv)
              setLoanBalance(newLoanBalance)
              /* We keep track of the maximum on the call for those specific values */
              /* We can do as many calls as we want after this, when loan amount changes */
              /* And loan amount changes when LTV is updated, or when it's directly updated */
              if (!maxLoanBalance.current || coreValuesHaveChanged) {
                maxLoanBalance.current = (newLoanBalance)
              }
              if (!maxLTV.current || coreValuesHaveChanged) {
                maxLTV.current = result.ltv
              }
              if (!minLTV.current || coreValuesHaveChanged) {
                minLTV.current = result.min_ltv ?? result.ltv
              }
              /* We also update this so it does not trigger a rerender */
              lastLoanBalance.current = newLoanBalance
              lastLtv.current = result.ltv
            }
            setIsLoading(false)
          }
        )
      }
    }
  }, [values, rental_loan_computation, loan_computation, selectedProgram, PROGRAMS, coreValuesHaveChanged, valuesHaveChanged])

  /* Updating LTV updates the loan amount */
  useEffect(() => {
    if (!lastLtv.current) {
      lastLtv.current = ltv
      return
    } else {
      if (ltv !== undefined && ltv !== lastLtv.current) {
        let newBalance = values["price"] * ltv
        if (isShortTermFixAndFlip(values)) {
          newBalance = newBalance + values["constructionBudget"]
        }
        if (isBelowMinLoanAmount(newBalance, values)) {
          newBalance = getMinLoanAmount(values)
        }
        setLoanBalance(newBalance)
      }
      lastLtv.current = ltv
    }
  }, [ltv, values])

  /* Updating loan balance triggers fetch */
  useEffect(() => {
    // Update ref with current values
    currentValuesRef.current = values

    if (!lastLoanBalance.current) {
      lastLoanBalance.current = loanBalance
      return
    } else {
      if (loanBalance !== undefined && loanBalance !== lastLoanBalance.current) {
        // Only update if the loanBalance value is actually different from what's in values
        if (currentValuesRef.current.loanBalance !== loanBalance) {
          setValues({...currentValuesRef.current, loanBalance})
        }
      }
      lastLoanBalance.current = loanBalance
    }
  }, [loanBalance, setValues])

  return {
    loanOptions,
    multipleLoanOptions,
    isLoading,
    loanBalance,
    setLoanBalance,
    maxLoanBalance: maxLoanBalance.current,
    min_ltv,
    minLTV: minLTV.current,
    max_ltv,
    maxLTV: maxLTV.current,
    ltv,
    setLtv,
    coreValuesHaveChanged,
  }
}

